/**
 * The project set-up wizard (batch 6: F13 project set-up for the office, F26 "so every setting and detail is right
 * for the field crew"; docs/specs/cloud/project-setup-wizard.md; help `cloud.project-setup`): a staff project's
 * "Set-up" tab. One step per part of the project, each with the quick-start checklist's own checks (Pass, Warning or
 * Fail with the word), what is wrong, the settings the office changes here (survey heights, the crew's heights, the
 * crew, the base mag position, the report emails with their words and contents, decisions 883 and 884) and a way to the page or Teslyn Field screen for the rest. A step is
 * confirmed once nothing in it fails; who confirmed it and when is kept, and Teslyn Field's Quick-start and training
 * page shows it to the crew.
 */
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  REPORT_EMAIL_MESSAGE_DEFAULT,
  REPORT_EMAIL_SETTING_ROLES,
  REPORT_EMAIL_SUBJECT_DEFAULT,
  appEmailSenderName,
  reportEmailSettingFull,
  reportEmailValues,
  reportEmailWording,
  reportEmailWordingProblems,
  reportEmailWordsToKeep,
  crewHeightsWarning,
  projectSetupProgress,
  projectSetupWizard,
  setupStepCounts,
  type ChecklistState,
  type ProjectSetupStep,
  type ReportEmailSettingFull,
} from '@teslyn/domain';
import type { PageId } from '../access/roles.ts';
import { Button } from '../components/Button.tsx';
import { CheckField, NumberField } from '../components/Fields.tsx';
import { ErrorState, Loading } from '../components/States.tsx';
import type { PortalProject } from '../portal/api.ts';
import { ProjectCrewPanel } from '../staff/ProjectCrewPanel.tsx';
import { loadTenantName } from '../email/api.ts';
import { ReportEmailContentField, ReportEmailWords } from '../email/ReportEmailWords.tsx';
import {
  confirmSetupStep,
  loadSetup,
  saveCrewHeights,
  savePositionCoordinates,
  saveReportEmails,
  saveSurveyHeight,
  undoSetupStep,
  type SetupData,
} from './api.ts';

export interface SetupWizardProps {
  readonly sb: SupabaseClient;
  readonly project: PortalProject;
  readonly roles: readonly string[];
  /** Open another Teslyn Cloud page (People, Aircraft, Station health, Alerts, Clients). */
  readonly onOpenPage?: (page: PageId) => void;
  /** The pages this person may open: only their buttons are shown (all when not given). */
  readonly openablePages?: readonly PageId[];
  /** Today (tests). */
  readonly today?: string;
}

const CHIP: Readonly<Record<ChecklistState, string>> = {
  pass: 'chip chip--pass label',
  warn: 'chip chip--warn label',
  fail: 'chip chip--fail label',
};

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));
const day = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });

/** The page each step's "Open …" button goes to, with its words. */
const STEP_PAGES: Readonly<Record<string, ReadonlyArray<readonly [PageId, string]>>> = {
  staff: [['people', 'Open People']],
  aircraft: [
    ['aircraft', 'Open Aircraft'],
    ['tracking', 'Open Live tracking'],
  ],
  basemag: [['stations', 'Open Station health']],
  alerts: [['alerts', 'Open Alerts']],
  client: [['clients', 'Open Clients']],
};

export function SetupWizard({
  sb,
  project,
  roles,
  onOpenPage,
  openablePages,
  today,
}: SetupWizardProps): ReactElement {
  const [data, setData] = useState<SetupData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [at, setAt] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ readonly ok: boolean; readonly text: string } | null>(null);
  const editable = roles.some((r) => REPORT_EMAIL_SETTING_ROLES.includes(r));

  const load = useCallback(() => {
    setError(null);
    loadSetup(sb, project, today)
      .then(setData)
      .catch((e: unknown) => setError(message(e)));
  }, [sb, project, today]);
  useEffect(load, [load]);

  async function act(what: string, run: () => Promise<string>): Promise<void> {
    setBusy(what);
    setNotice(null);
    try {
      const text = await run();
      setNotice({ ok: true, text });
      load();
    } catch (e) {
      setNotice({ ok: false, text: message(e) });
    } finally {
      setBusy(null);
    }
  }

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <Loading what="the project's set-up" />;
  const steps = projectSetupWizard(data.input);
  const progress = projectSetupProgress(data.input.confirmed);
  const step = steps[Math.min(at, steps.length - 1)]!;
  const pages = (STEP_PAGES[step.id] ?? []).filter(([p]) => !openablePages || openablePages.includes(p));

  return (
    <div className="stack setup-wizard">
      <div className="panel stack">
        <h2 className="heading">Project set-up</h2>
        <p className="body">
          Go through each step before the crew leaves, so every setting and detail is right for the field.
          Each check is the one the crew sees in Teslyn Field&apos;s Quick-start and training.
        </p>
        <p className="body" role="status">
          {progress.text}
        </p>
        <ol className="steps setup-wizard__steps" aria-label="Set-up steps">
          {steps.map((s, i) => (
            <li key={s.id}>
              <button
                type="button"
                className={i === at ? 'steps__item steps__item--on' : 'steps__item'}
                aria-current={i === at ? 'step' : undefined}
                onClick={() => {
                  setAt(i);
                  setNotice(null);
                }}
              >
                {i + 1}. {s.title} <span className={CHIP[s.state]}>{wordOf(s)}</span>
                {s.confirmed && <span className="steps__count"> · Confirmed</span>}
              </button>
            </li>
          ))}
        </ol>
      </div>

      <section className="panel stack" aria-labelledby={`setup-${step.id}`}>
        <h3 className="heading" id={`setup-${step.id}`}>
          Step {at + 1} of {steps.length}: {step.title}
        </h3>
        <p className="body">{step.intro}</p>
        <ul className="setup-wizard__items">
          {step.items.map((i) => (
            <li key={i.id} className="setup-wizard__item">
              <span className={CHIP[i.state]}>{i.word}</span>
              <div className="stack setup-wizard__text">
                <span className="body setup-wizard__title">{i.title}</span>
                <span className="caption muted">{i.found}</span>
                {i.problems.length > 0 && (
                  <ul className="issues">
                    {i.problems.map((p) => (
                      <li key={p} className="body">
                        {p}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </li>
          ))}
        </ul>

        <StepEditor
          sb={sb}
          project={project}
          roles={roles}
          data={data}
          step={step}
          editable={editable}
          busy={busy}
          act={act}
        />

        {onOpenPage && pages.length > 0 && (
          <div className="row row--wrap">
            {pages.map(([page, label]) => (
              <Button key={page} variant="quiet" onClick={() => onOpenPage(page)}>
                {label}
              </Button>
            ))}
          </div>
        )}

        {notice && (
          <p className={notice.ok ? 'notice' : 'notice notice--fail'} role={notice.ok ? 'status' : 'alert'}>
            {notice.text}
          </p>
        )}

        {step.confirmed && (
          <p className="body">
            Confirmed by {step.confirmed.confirmed_by_name ?? 'someone no longer in the organisation'} on{' '}
            {day(step.confirmed.confirmed_at)}.
          </p>
        )}
        {editable && step.blocked && !step.confirmed && <p className="caption muted">{step.blocked}</p>}
        <div className="row row--wrap row--between">
          <div className="row row--wrap">
            <Button disabled={at === 0} onClick={() => setAt(at - 1)}>
              Back
            </Button>
            <Button disabled={at >= steps.length - 1} onClick={() => setAt(at + 1)}>
              Next
            </Button>
          </div>
          {editable && (
            <div className="row row--wrap">
              {step.confirmed && (
                <Button
                  variant="quiet"
                  busy={busy === 'undo'}
                  onClick={() =>
                    void act('undo', async () => {
                      await undoSetupStep(sb, project.id, step.id);
                      return `"${step.title}" is no longer confirmed.`;
                    })
                  }
                >
                  Undo confirmation
                </Button>
              )}
              <Button
                variant="primary"
                busy={busy === 'confirm'}
                disabled={step.blocked !== null}
                title={step.blocked ?? undefined}
                onClick={() =>
                  void act('confirm', async () => {
                    await confirmSetupStep(sb, project, step.id, setupStepCounts(step));
                    if (at < steps.length - 1) setAt(at + 1);
                    return `"${step.title}" is confirmed.`;
                  })
                }
              >
                {step.confirmed ? 'Confirm again' : 'Confirm this step'}
              </Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function wordOf(s: ProjectSetupStep): string {
  return s.state === 'fail' ? 'Fail' : s.state === 'warn' ? 'Warning' : 'Pass';
}

interface EditorProps {
  readonly sb: SupabaseClient;
  readonly project: PortalProject;
  readonly roles: readonly string[];
  readonly data: SetupData;
  readonly step: ProjectSetupStep;
  readonly editable: boolean;
  readonly busy: string | null;
  readonly act: (what: string, run: () => Promise<string>) => Promise<void>;
}

/** The settings a step changes in Teslyn Cloud itself; the rest are named where they are changed. */
function StepEditor(props: EditorProps): ReactElement | null {
  const { step } = props;
  if (step.id === 'blocks')
    return (
      <p className="caption muted">
        The zone is read from each block&apos;s lines file when the crew imports the final flight plan in
        Teslyn Field, Flight planning. Blocks and their spacing come from the contract.
      </p>
    );
  if (step.id === 'heights') return <HeightsEditor {...props} />;
  if (step.id === 'staff')
    return (
      <ProjectCrewPanel
        sb={props.sb}
        tenantId={props.project.tenantId}
        projectId={props.project.id}
        roles={props.roles}
      />
    );
  if (step.id === 'aircraft')
    return (
      <p className="caption muted">
        Aircraft and their figures are kept on the Aircraft page (or in Teslyn Field, Project settings,
        Aircraft); trackers on Live tracking&apos;s settings.
      </p>
    );
  if (step.id === 'basemag') return <BaseMagEditor {...props} />;
  if (step.id === 'alerts')
    return (
      <p className="caption muted">
        The alerts go to the operators on the crew (step 3) while their crew dates are in force: give each
        operator an end date, or the alerts follow them home on break. Each person turns push on for their own
        phone in Teslyn Cloud, Alerts.
      </p>
    );
  if (step.id === 'client') return <ReportEmailsEditor {...props} />;
  return (
    <p className="caption muted">
      In Teslyn Field: Quick-start and training checks these on the laptop, and its first flight wizard reads
      the first Zdas log before it is imported.
    </p>
  );
}

function HeightsEditor({ sb, data, editable, busy, act, project }: EditorProps): ReactElement {
  const survey = data.blocks.filter((b) => b.deleted_at === null && b.kind === 'survey');
  const [heights, setHeights] = useState<Record<string, number | null>>(() =>
    Object.fromEntries(survey.map((b) => [b.id, b.terrain_clearance_m ?? null])),
  );
  const [deck, setDeck] = useState<number | null>(data.project.qc_hard_deck_m ?? null);
  const [lwh, setLwh] = useState<number | null>(data.project.qc_lower_working_m ?? null);
  const swapped = crewHeightsWarning(deck, lwh);
  return (
    <div className="stack">
      <h4 className="label">Survey height of each block (sensor height)</h4>
      <div className="form-grid">
        {survey.map((b) => (
          <NumberField
            key={b.id}
            label={b.name}
            unit="m"
            value={heights[b.id] ?? null}
            disabled={!editable}
            onChange={(v) => setHeights((h) => ({ ...h, [b.id]: v }))}
          />
        ))}
      </div>
      {editable && (
        <div className="row">
          <Button
            busy={busy === 'heights'}
            onClick={() =>
              void act('heights', async () => {
                for (const b of survey) {
                  const v = heights[b.id] ?? null;
                  if (v !== (b.terrain_clearance_m ?? null)) await saveSurveyHeight(sb, b.id, v);
                }
                return 'Survey heights saved. Teslyn Field receives them with the project.';
              })
            }
          >
            Save survey heights
          </Button>
        </div>
      )}
      <h4 className="label">The crew&apos;s heights (set after the safety recce flight)</h4>
      <div className="form-grid">
        <NumberField label="Hard deck" unit="m" value={deck} disabled={!editable} onChange={setDeck} />
        <NumberField
          label="Lower working height"
          unit="m"
          value={lwh}
          disabled={!editable}
          onChange={setLwh}
        />
      </div>
      {swapped && <p className="caption">{swapped}</p>}
      {editable && (
        <div className="row">
          <Button
            busy={busy === 'crew-heights'}
            onClick={() =>
              void act('crew-heights', async () => {
                await saveCrewHeights(sb, project.id, { qc_hard_deck_m: deck, qc_lower_working_m: lwh });
                return 'Hard deck and lower working height saved.';
              })
            }
          >
            Save the crew&apos;s heights
          </Button>
        </div>
      )}
      <p className="caption muted">
        The terrain clearance and flight path limits come from the contract; change them in Teslyn Field,
        Project settings, QC limits.
      </p>
    </div>
  );
}

function BaseMagEditor({ sb, data, editable, busy, act }: EditorProps): ReactElement {
  const bases = data.positions.filter((p) => p.deleted_at === null && p.kind === 'base_mag');
  const [coords, setCoords] = useState<Record<string, { e: number | null; n: number | null }>>(() =>
    Object.fromEntries(bases.map((p) => [p.id, { e: p.easting ?? null, n: p.northing ?? null }])),
  );
  if (bases.length === 0)
    return (
      <p className="caption muted">
        Add the base mag position in Teslyn Field, Project settings, Positions (or from the base
        station&apos;s GPS file there).
      </p>
    );
  return (
    <div className="stack">
      {bases.map((p) => (
        <div key={p.id} className="stack">
          <h4 className="label">{p.name}</h4>
          <div className="form-grid">
            <NumberField
              label="Easting"
              unit="m"
              value={coords[p.id]?.e ?? null}
              disabled={!editable}
              onChange={(v) => setCoords((c) => ({ ...c, [p.id]: { e: v, n: c[p.id]?.n ?? null } }))}
            />
            <NumberField
              label="Northing"
              unit="m"
              value={coords[p.id]?.n ?? null}
              disabled={!editable}
              onChange={(v) => setCoords((c) => ({ ...c, [p.id]: { e: c[p.id]?.e ?? null, n: v } }))}
            />
          </div>
          {editable && (
            <div className="row">
              <Button
                busy={busy === `pos:${p.id}`}
                onClick={() =>
                  void act(`pos:${p.id}`, async () => {
                    await savePositionCoordinates(sb, p.id, coords[p.id]?.e ?? null, coords[p.id]?.n ?? null);
                    return `${p.name}'s position saved.`;
                  })
                }
              >
                Save position
              </Button>
            </div>
          )}
        </div>
      ))}
      <p className="caption muted">
        Coordinates in the project&apos;s grid. On the laptop, Project settings, Positions reads them from the
        base station&apos;s GPS file.
      </p>
    </div>
  );
}

function ReportEmailsEditor({ sb, project, data, editable, busy, act }: EditorProps): ReactElement {
  const start = reportEmailSettingFull(data.input.reportEmails);
  const [s, setS] = useState<ReportEmailSettingFull>(start);
  const words = reportEmailWording(data.input.reportEmails);
  const [subject, setSubject] = useState(words.subject);
  const [messageText, setMessageText] = useState(words.message);
  const [problems, setProblems] = useState<readonly string[]>([]);
  const [tenantName, setTenantName] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    void loadTenantName(sb, project.tenantId).then((n) => live && setTenantName(n));
    return () => {
      live = false;
    };
  }, [sb, project.tenantId]);
  const organisation = tenantName ?? 'your organisation';
  return (
    <div className="stack">
      {!project.clientOrgId && (
        <p className="caption muted">
          Choose the client organisation under &quot;Client organisation&quot; above.
        </p>
      )}
      <fieldset className="step stack">
        <legend className="label">Email the client when a report is published</legend>
        <CheckField
          label="Daily reports"
          checked={s.daily}
          disabled={!editable}
          onChange={(v) => setS({ ...s, daily: v })}
        />
        <CheckField
          label="Weekly reports"
          checked={s.weekly}
          disabled={!editable}
          onChange={(v) => setS({ ...s, weekly: v })}
        />
        <CheckField
          label="With a weekly report, also the day's daily report (its link and PDF)"
          checked={s.weekly_with_daily}
          disabled={!editable}
          onChange={(v) => setS({ ...s, weekly_with_daily: v })}
        />
      </fieldset>
      <ReportEmailContentField
        value={s.content}
        disabled={!editable}
        onChange={(v) => setS({ ...s, content: v })}
      />
      <fieldset className="step stack">
        <legend className="label">The email&apos;s words</legend>
        <ReportEmailWords
          subject={subject}
          message={messageText}
          onSubject={setSubject}
          onMessage={setMessageText}
          disabled={!editable}
          fallback={{
            subject: REPORT_EMAIL_SUBJECT_DEFAULT,
            message: REPORT_EMAIL_MESSAGE_DEFAULT,
            name: "Teslyn's wording",
          }}
          preview={reportEmailValues({
            kind: 'daily',
            period: new Date().toISOString().slice(0, 10),
            revision: 1,
            projectNumber: project.number,
            projectTitle: project.title,
            tenantName: organisation,
            clientOrgName: project.clientOrgName ?? 'the client',
            recipientName: "(the person's name)",
            publisherName: '(who publishes it)',
            cloudUrl: '',
          })}
        />
      </fieldset>
      <p className="caption muted">
        Each person at the client is emailed once per report, from &quot;{appEmailSenderName(tenantName)}
        &quot;, with replies to whoever published it. Whoever publishes can change the words and what the
        email carries for that report. Each person at the client can turn the emails off in My sign-in.
      </p>
      {problems.length > 0 && (
        <ul className="notice notice--fail" role="alert">
          {problems.map((p) => (
            <li key={p} className="body">
              {p}
            </li>
          ))}
        </ul>
      )}
      {editable && (
        <div className="row">
          <Button
            busy={busy === 'emails'}
            onClick={() => {
              const p = reportEmailWordingProblems({ subject, message: messageText });
              setProblems(p);
              if (p.length > 0) return;
              void act('emails', async () => {
                await saveReportEmails(sb, project, {
                  ...s,
                  subject: reportEmailWordsToKeep(subject, REPORT_EMAIL_SUBJECT_DEFAULT),
                  message: reportEmailWordsToKeep(messageText, REPORT_EMAIL_MESSAGE_DEFAULT),
                });
                return 'Report emails saved.';
              });
            }}
          >
            Save report emails
          </Button>
        </div>
      )}
    </div>
  );
}
